import { array } from 'fp-ts';
import { connect } from 'react-redux';
import { flow } from 'fp-ts/function';
import { Badge, CardContent, ClickableDiv, Divider, ExpandIcon, Stack } from '@optimus-web/core';
import { LocalizedLabel, SpacingMediumTop } from '@mews-commander/ui';
import { ExpandableBox } from '@optimus-web/helpers';
import { ApplicationState, ServiceOrderNote } from '@mews-commander/types';
import { intersperse } from '@mews/fp';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { useToggle } from '@mews/framework';
import { getNotesSortedByServiceOrderId } from '@mews-commander/legacy-entities/src/ducks/serviceOrderNotes';
import preferences from '../../../../preferences';
import NotesListItem from './NotesListItem';

const {
    actions: { setPreferredNotesExpanded },
    selectors: { getPreferredNotesExpanded },
} = preferences;

interface StateProps {
    notes: ServiceOrderNote[];
    expandByDefault: boolean;
}

interface OwnProps {
    serviceOrderId: string;
}

interface DispatchProps {
    saveNotesPreference: (expanded: boolean) => void;
}

const renderNotes = flow(
    array.map((note: ServiceOrderNote) => <NotesListItem note={note} key={note.id} />),
    intersperse((index: number) => <Divider key={index} />)
);

const NotesList = ({ notes, expandByDefault, saveNotesPreference }: StateProps & DispatchProps) => {
    const [isToggled, toggle] = useToggle({ initialValue: expandByDefault });
    const handleToggle = () => {
        saveNotesPreference(!isToggled);
        toggle();
    };

    const hasNotes = notes.length > 0;

    const label = (
        <Stack spacing="100" itemAlignment="center">
            <LocalizedLabel textKey={TextKey.Notes} as="div" />
            <Badge content={notes.length} data-test-smart-detail-notes-badge />
        </Stack>
    );

    const content = hasNotes ? (
        <>
            <ClickableDiv onClick={handleToggle} data-test-id={DataTestId.SmartDetailNotesExpandButton}>
                <Stack distribution="space-between" itemAlignment="center">
                    {label}
                    <ExpandIcon size="small" expanded={isToggled} />
                </Stack>
            </ClickableDiv>

            <ExpandableBox expanded={isToggled}>
                <SpacingMediumTop>
                    <Stack spacing="100" vertical>
                        {renderNotes(notes)}
                    </Stack>
                </SpacingMediumTop>
            </ExpandableBox>
        </>
    ) : (
        <div>{label}</div>
    );

    return (
        <>
            <Divider />

            <CardContent padding="200">{content}</CardContent>
        </>
    );
};

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state, { serviceOrderId }) => ({
        notes: getNotesSortedByServiceOrderId(state, { serviceOrderId }),
        expandByDefault: getPreferredNotesExpanded(state),
    }),
    (dispatch) => ({
        saveNotesPreference: (expanded) => dispatch(setPreferredNotesExpanded(expanded)),
    })
)(NotesList);
